import React from "react";
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    IconButton, 
    Tooltip,
    useMediaQuery,
    useTheme
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import translate from "../i18n/Translator";

const useStyles = makeStyles((theme) => ({
    card: {
        height: "100%",
        position: "relative",
        borderRadius: theme.spacing(1),
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, 1), 
        padding: theme.spacing(1), 
    },
    cardContent: {
        padding: theme.spacing(1.5),
        "&:last-child": {
            paddingBottom: theme.spacing(1.5)
        }
    },
    headerContainer: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: theme.spacing(1.5),
        minHeight: 32, 
        position: "relative"
    },
    titleContainer: {
        flex: 1,
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        textAlign: "center",
        [theme.breakpoints.down('xs')]: {
            paddingRight: theme.spacing(0.5),
            paddingLeft: theme.spacing(0.5),
        }
    },
    title: {
        fontWeight: 600,
        fontSize: "0.875rem",
        lineHeight: 1.3,
        color: theme.palette.text.primary,
        [theme.breakpoints.down('sm')]: {
            fontSize: "0.8rem",
            lineHeight: 1.2,
        }
    },
    statusIconContainer: {
        position: "absolute",
        left: 0,
        top: 0,
        zIndex: 1
    },
    statusIcon: {
        fontSize: 20,
        [theme.breakpoints.down('sm')]: {
            fontSize: 18
        }
    },
    warningIcon: {
        color: theme.palette.warning.main
    },
    successIcon: {
        color: theme.palette.success.main
    },
    breakdownButtonContainer: {
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 1
    },
    breakdownButton: {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.text.secondary,
        width: 32,
        height: 32,
        "&:hover": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText
        },
        [theme.breakpoints.down('sm')]: {
            width: 28,
            height: 28,
        }
    },
    mainAmount: {
        fontWeight: "bold",
        fontSize: "1.25rem",
        lineHeight: 1.2,
        color: theme.palette.text.primary,
        textAlign: "center"
    },
    secondaryAmount: {
        color: theme.palette.warning.main,
        fontSize: "0.875rem",
        fontWeight: 500,
        textAlign: "center",
        marginTop: theme.spacing(0.5)
    },
    count: {
        fontWeight: "bold",
        fontSize: "1.75rem",
        lineHeight: 1,
        color: theme.palette.text.primary,
        textAlign: "center"
    },
    secondaryCount: {
        color: theme.palette.warning.main,
        fontSize: "0.75rem",
        fontWeight: 500,
        textAlign: "center",
        display: "block",
        marginTop: theme.spacing(0.5)
    },
    amountContainer: {
        marginBottom: theme.spacing(1)
    },
    countContainer: {
        marginTop: theme.spacing(1)
    },
    dataContainer: {
        display: "flex",
        justifyContent: "space-around",
        alignItems: "flex-start",
        marginTop: theme.spacing(1)
    },
    column: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        padding: theme.spacing(0, 0.5) 
    },
    separator: {
        width: 1,
        backgroundColor: theme.palette.divider,
        margin: theme.spacing(0, 1)
    }
}));

interface InformativeCardProps {
    title: string;
    amounts?: { [currency: string]: number };
    cancellationAmounts?: { [currency: string]: number };
    count?: number;
    cancellationCount?: number;
    secondaryText?: string;
    hasDiscrepancy: boolean;
    showBreakdownButton?: boolean;
    onViewBreakdown?: () => void;
}

export default function InformativeCard({
    title,
    amounts,
    cancellationAmounts,
    count,
    cancellationCount,
    secondaryText,
    hasDiscrepancy,
    showBreakdownButton = false,
    onViewBreakdown
}: InformativeCardProps) {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formatAmount = (amount: number, currency: string): string => {
        const formatted = new Intl.NumberFormat('es-MX', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true
        }).format(amount);
        return `$ ${formatted} ${currency}`;
    };

    const renderAmounts = () => {
        if (!amounts) return null;
        
        return Object.entries(amounts).map(([currency, amount]) => {
            const cancellationAmount = cancellationAmounts?.[currency] ?? 0;
            
            return (
                <Box key={currency} className={classes.amountContainer}>
                    <Typography variant="h6" className={classes.mainAmount}>
                        {formatAmount(amount, currency)}
                    </Typography>
                    {cancellationAmounts !== undefined && (
                        <Typography variant="body2" className={classes.secondaryAmount}>
                            {formatAmount(cancellationAmount, currency)}
                        </Typography>
                    )}
                </Box>
            );
        });
    };

    const renderCount = () => {
        if (count === undefined) return null;
        
        return (
            <Box className={classes.countContainer}>
                <Typography variant="h4" className={classes.count}>
                    {count.toLocaleString('es-MX')}
                </Typography>
                {cancellationCount !== undefined && secondaryText && (
                    <Typography variant="caption" className={classes.secondaryCount}>
                        {cancellationCount.toLocaleString('es-MX')} {secondaryText}
                    </Typography>
                )}
            </Box>
        );
    };

    const shouldShowStatusIcon = (): boolean => {
        return cancellationAmounts !== undefined || cancellationCount !== undefined;
    };

    const getStatusIcon = () => {
        if (!shouldShowStatusIcon()) return null;

        const IconComponent = hasDiscrepancy ? WarningIcon : CheckCircleIcon;
        const iconClass = hasDiscrepancy ? classes.warningIcon : classes.successIcon;
        const tooltipText = hasDiscrepancy 
            ? translate("cfdis.cfdis_non_integrated.tooltips.status_warning") as string
            : translate("cfdis.cfdis_non_integrated.tooltips.status_success") as string;

        return (
            <div className={classes.statusIconContainer}>
                <Tooltip title={tooltipText}>
                    <IconComponent className={`${classes.statusIcon} ${iconClass}`} />
                </Tooltip>
            </div>
        );
    };

    const getBreakdownButton = () => {
        if (!showBreakdownButton || !onViewBreakdown) return null;

        return (
            <div className={classes.breakdownButtonContainer}>
                <Tooltip title={translate("cfdis.cfdis_non_integrated.tooltips.view_breakdown") as string}>
                    <IconButton 
                        className={classes.breakdownButton}
                        onClick={onViewBreakdown}
                        size="small"
                    >
                        <ArrowForwardIcon fontSize={isMobile ? "small" : "small"} />
                    </IconButton>
                </Tooltip>
            </div>
        );
    };

    return (
        <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
                <Box className={classes.headerContainer}>
                    {getStatusIcon()}
                    
                    <Box className={classes.titleContainer}>
                        <Typography variant="subtitle2" className={classes.title}>
                            {title}
                        </Typography>
                    </Box>
                    
                    {getBreakdownButton()}
                </Box>
                
                <Box className={classes.dataContainer}>
                    <Box className={classes.column}>
                        {amounts ? renderAmounts() : renderCount()}
                    </Box>
                    
                    {count !== undefined && amounts && (
                        <>
                            <div className={classes.separator} />
                            <Box className={classes.column}>
                                {renderCount()}
                            </Box>
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}